// ============================================================
// FILE: EditJob.js
// LOCATION: C:\gardenmarket\web\src\pages\buyer\EditJob.js
// ============================================================
// PURPOSE:
//   Buyer's job edit page. Allows editing title, description,
//   date, budget, skills, and (NEW) job posting photos while
//   the job is still editable (draft/open/matched).
//
//   Once a job reaches 'completed' or 'reviewed', the entire
//   form renders as read-only — the buyer can see the job
//   details but cannot change anything.
//
// ROUTE: /buyer/job/:id/edit
//
// CALLS API:
//   GET  /api/jobs/:id           — load current job details
//   PUT  /api/jobs/:id           — save edits
//   POST /api/photos/job/:id     — upload photos (via PhotoUploader)
//   DELETE /api/photos/job/:id   — delete a photo (via PhotoUploader)
//
// READ-ONLY STATES:
//   completed, reviewed — form fields disabled, no save button,
//   photos shown as a read-only gallery (no upload/delete)
//
// EDITABLE STATES:
//   draft, open, matched, quoted — all fields editable.
//   (Photos also uploadable at these stages per the updated
//   usp_job_photo_add proc which allows 'matched'/'quoted'.)
// ============================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import client from '../../api/client';
import PhotoUploader from '../../components/PhotoUploader';

const EDITABLE_STATUSES = ['draft', 'open', 'matched', 'quoted'];

export default function EditJob() {
    const { id }   = useParams();
    const navigate = useNavigate();

    const [job,     setJob]     = useState(null);
    const [skills,  setSkills]  = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [error,   setError]   = useState('');
    const [success, setSuccess] = useState('');
    const [photos,  setPhotos]  = useState([]);

    const [form, setForm] = useState({
        title:          '',
        description:    '',
        preferred_date: '',
        budget:         '',
        skill_ids:      [],
    });

    const loadJob = async () => {
        const [jobRes, skillsRes] = await Promise.all([
            client.get(`/jobs/${id}`),
            client.get('/ref/skills'),
        ]);
        const j = jobRes.data.job;
        setJob(j);
        setSkills(skillsRes.data.skills);
        setPhotos((j.photos || []).filter(p => !p.photo_type || p.photo_type === 'job_posting'));
        setForm({
            title:          j.title || '',
            description:    j.description || '',
            preferred_date: j.preferred_date ? j.preferred_date.split('T')[0] : '',
            budget:         j.budget || '',
            skill_ids:      (j.skills || []).map(s => s.skill_id),
        });
    };

    useEffect(() => {
        loadJob().catch(err => setError(err.response?.data?.error || 'Failed to load job.'))
                 .finally(() => setLoading(false));
    }, [id]);

    const toggleSkill = (skillId) => {
        setForm(f => ({
            ...f,
            skill_ids: f.skill_ids.includes(skillId)
                ? f.skill_ids.filter(s => s !== skillId)
                : [...f.skill_ids, skillId],
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            await client.put(`/jobs/${id}`, {
                title:          form.title,
                description:    form.description,
                preferred_date: form.preferred_date || null,
                budget:         form.budget ? parseFloat(form.budget) : null,
                skill_ids:      form.skill_ids,
            });
            setSuccess('Job updated successfully.');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update job.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.loading}>Loading...</div>;
    if (!job)    return <div style={styles.loading}>{error}</div>;

    const isEditable = EDITABLE_STATUSES.includes(job.status_code);
    const isReadOnly = !isEditable;

    // Group skills by category for the skills picker
    const grouped = skills.reduce((acc, s) => {
        if (!acc[s.category]) acc[s.category] = [];
        acc[s.category].push(s);
        return acc;
    }, {});

    return (
        <div style={styles.page}>
            <div style={styles.backRow}>
                <Link to="/buyer/dashboard" style={styles.back}>← Back to My Jobs</Link>
            </div>

            <div style={styles.headerRow}>
                <h1 style={styles.title}>
                    {isReadOnly ? 'Job Details' : 'Edit Job'}
                    <span style={styles.jobId}>#{job.job_id}</span>
                </h1>
                {isReadOnly && (
                    <span style={styles.readOnlyBadge}>
                        {job.status_code === 'reviewed' ? '✓ Reviewed' : '✓ Completed'} — read only
                    </span>
                )}
            </div>

            {error   && <div style={styles.error}>{error}</div>}
            {success && <div style={styles.success}>{success}</div>}

            <form onSubmit={isReadOnly ? e => e.preventDefault() : handleSave} style={styles.form}>

                {/* ── Photos section ──────────────────────────────── */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Job Photos</h3>
                    {isEditable ? (
                        <PhotoUploader
                            uploadUrl={`/photos/job/${id}`}
                            extraFields={{ photo_type: 'job_posting' }}
                            existingPhotos={photos}
                            onDeleteUrl={(photo) => `/photos/job/${photo.photo_id}`}
                            onChange={async () => {
                                const r = await client.get(`/jobs/${id}`);
                                setPhotos((r.data.job.photos || []).filter(p => !p.photo_type || p.photo_type === 'job_posting'));
                            }}
                            label=""
                            maxFiles={9}
                        />
                    ) : (
                        photos.length > 0 ? (
                            <div style={styles.photoGrid}>
                                {photos.map(p => (
                                    <img key={p.photo_id} src={p.storage_url}
                                         alt={p.caption || 'Job photo'}
                                         style={styles.photo}
                                         onError={e => e.target.style.display='none'} />
                                ))}
                            </div>
                        ) : (
                            <p style={styles.noPhotos}>No photos were added to this job.</p>
                        )
                    )}
                </div>

                {/* ── Job details ─────────────────────────────────── */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Job Details</h3>

                    <div style={styles.field}>
                        <label style={styles.label}>Title</label>
                        <input
                            value={form.title}
                            onChange={e => setForm({...form, title: e.target.value})}
                            style={{...styles.input, ...(isReadOnly ? styles.readOnlyInput : {})}}
                            disabled={isReadOnly}
                            required={!isReadOnly}
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Description</label>
                        <textarea
                            value={form.description}
                            onChange={e => setForm({...form, description: e.target.value})}
                            style={{...styles.input, height:'110px', resize:'vertical', ...(isReadOnly ? styles.readOnlyInput : {})}}
                            disabled={isReadOnly}
                        />
                    </div>

                    <div style={styles.row}>
                        <div style={styles.field}>
                            <label style={styles.label}>Preferred date</label>
                            <input
                                type="date"
                                value={form.preferred_date}
                                onChange={e => setForm({...form, preferred_date: e.target.value})}
                                style={{...styles.input, ...(isReadOnly ? styles.readOnlyInput : {})}}
                                disabled={isReadOnly}
                            />
                        </div>
                        <div style={styles.field}>
                            <label style={styles.label}>Budget ($)</label>
                            <input
                                type="number" min="0"
                                value={form.budget}
                                onChange={e => setForm({...form, budget: e.target.value})}
                                style={{...styles.input, ...(isReadOnly ? styles.readOnlyInput : {})}}
                                disabled={isReadOnly}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Skills ──────────────────────────────────────── */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Skills Required</h3>
                    {Object.entries(grouped).map(([cat, catSkills]) => (
                        <div key={cat} style={{marginBottom:'12px'}}>
                            <div style={styles.skillCat}>{cat}</div>
                            <div style={styles.chips}>
                                {catSkills.map(s => (
                                    <button
                                        key={s.skill_id}
                                        type="button"
                                        onClick={() => !isReadOnly && toggleSkill(s.skill_id)}
                                        style={form.skill_ids.includes(s.skill_id)
                                            ? {...styles.chipActive, ...(isReadOnly ? styles.chipReadOnly : {})}
                                            : {...styles.chip, ...(isReadOnly ? styles.chipReadOnly : {})}}
                                    >
                                        {s.skill_name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Save button — hidden for read-only jobs */}
                {!isReadOnly && (
                    <button type="submit" style={styles.btnSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                )}
            </form>
        </div>
    );
}

const styles = {
    page:          { maxWidth:'720px', margin:'0 auto', padding:'32px 24px' },
    loading:       { textAlign:'center', padding:'60px', color:'#666' },
    backRow:       { marginBottom:'16px' },
    back:          { color:'#2d6a4f', fontSize:'0.9rem' },
    headerRow:     { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' },
    title:         { color:'#2d6a4f', margin:0, fontSize:'1.5rem', fontWeight:700 },
    jobId:         { color:'#aaa', fontWeight:400, fontSize:'1rem', marginLeft:'8px' },
    readOnlyBadge: { fontSize:'0.8rem', backgroundColor:'#e8f5e9', color:'#2d6a4f', padding:'5px 12px', borderRadius:'12px', fontWeight:600 },
    error:         { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:'6px', padding:'10px 14px', color:'#c0392b', marginBottom:'16px', fontSize:'0.88rem' },
    success:       { backgroundColor:'#e8f5e9', border:'1px solid #66bb6a', borderRadius:'6px', padding:'10px 14px', color:'#2d6a4f', marginBottom:'16px', fontSize:'0.88rem' },
    form:          { display:'flex', flexDirection:'column', gap:'16px' },
    card:          { background:'white', borderRadius:'12px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.07)' },
    cardTitle:     { fontSize:'0.95rem', fontWeight:700, color:'#2d6a4f', marginBottom:'14px' },
    field:         { marginBottom:'14px' },
    row:           { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' },
    label:         { display:'block', fontWeight:600, marginBottom:'6px', color:'#333', fontSize:'0.88rem' },
    input:         { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'0.95rem', boxSizing:'border-box' },
    readOnlyInput: { backgroundColor:'#f9f9f9', color:'#666', cursor:'default' },
    skillCat:      { fontSize:'0.78rem', fontWeight:700, color:'#888', textTransform:'uppercase', marginBottom:'6px' },
    chips:         { display:'flex', flexWrap:'wrap', gap:'8px' },
    chip:          { padding:'5px 12px', borderRadius:'16px', border:'1px solid #ddd', background:'white', cursor:'pointer', fontSize:'0.85rem' },
    chipActive:    { padding:'5px 12px', borderRadius:'16px', border:'1px solid #2d6a4f', background:'#e8f5e9', color:'#2d6a4f', cursor:'pointer', fontSize:'0.85rem', fontWeight:'bold' },
    chipReadOnly:  { cursor:'default', opacity:0.8 },
    photoGrid:     { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px' },
    photo:         { width:'100%', height:'80px', objectFit:'cover', borderRadius:'6px' },
    noPhotos:      { color:'#999', fontSize:'0.88rem' },
    btnSave:       { width:'100%', padding:'13px', backgroundColor:'#2d6a4f', color:'white', border:'none', borderRadius:'8px', fontSize:'1rem', fontWeight:'bold', cursor:'pointer' },
};
